// KÖZÖS árazó-form primitívek (feature-független) — a szolgáltatás-KATALÓGUS (Phase-1) és a szolgáltatás-
// HOZZÁRENDELÉS (Phase-2 ár-override) egyaránt erre a rétegre épül. Itt élnek az árazó enum-listák, a
// pricingType→priceType mátrix-helperek, az ár-sor ↔ form-mező leképezés (buildPriceRows / pricesToForm) és a
// közös form-érték típusok. A shared PricingEditor komponens és mindkét feature form.ts-e innen fogyaszt.
//
// A pénz DECIMAL-STRING (soha float); a tier/overflow mezők egészek (string-ként a mezőben). A VÉGSŐ validáló-fal
// a szerver (BFF/Odin) — itt csak a form-alak + a determinisztikus leképezés van.
import { type ServicePlanPriceSchema } from '@atlas/contracts';
import { type z } from 'zod';
import { z as zod } from 'zod';

// ── Enum-listák (a @atlas/contracts enumjaival érték-azonosak; a body-validálás a contractból jön) ──
export const CURRENCIES = ['HUF', 'EUR', 'USD'] as const;
export const PRICING_TYPES = ['flat', 'tiered', 'base_tiered', 'commission'] as const;
export const VAT_RATES = [
  'VAT_27',
  'VAT_18',
  'VAT_5',
  'VAT_0',
  'AAM',
  'TAM',
  'FAA',
  'EUFAD37',
] as const;

export type Currency = (typeof CURRENCIES)[number];
export type PricingType = (typeof PRICING_TYPES)[number];
export type VatRate = (typeof VAT_RATES)[number];

/** Egy ki-menő ár-sor (a contract `ServicePlanPriceSchema` INPUT-alakja — id nélkül, új sor). */
export type PriceRow = z.input<typeof ServicePlanPriceSchema>;

// ── pricingType→priceType mátrix-helperek ──

/** A pricingType „elsődleges" (egy-darabos) ártétel-típusa: flat→unit, tiered→passive, base_tiered→base,
 *  commission→minimum. Ez az ár minden csomagon kötelező (akár 0 áron a passive/minimum). */
export function primaryPriceType(pt: PricingType): 'unit' | 'passive' | 'base' | 'minimum' {
  switch (pt) {
    case 'flat':
      return 'unit';
    case 'tiered':
      return 'passive';
    case 'base_tiered':
      return 'base';
    case 'commission':
      return 'minimum';
  }
}

/** Van-e a pricingType-nak sávos (tier) + opcionális overflow blokkja (tiered / base_tiered). */
export function hasTiers(pt: PricingType): boolean {
  return pt === 'tiered' || pt === 'base_tiered';
}

// ── Form-érték típusok ──

export interface TierFormValues {
  /** Sáv-minimum (egész, string-ként a mezőben). */
  tierMin: string;
  /** Sáv-maximum (egész, string; '' = korlátlan → az adatban null). */
  tierMax: string;
  /** Sáv nettó ára (DECIMAL-string). */
  netPrice: string;
  /** Sáv-deviza (eltérhet az alap/passive devizájától). */
  currency: Currency;
}

/**
 * A KÖZÖS árazó-blokk form-értékei (a PricingEditor komponens ezt szerkeszti). A katalógus `PlanFormValues`-a
 * ezt bővíti (név/kategória/stb.), a hozzárendelés-ár-override pedig ezt + a `vatRate`-et használja.
 */
export interface PricingFormValues {
  pricingType: PricingType;
  /** Az „elsődleges" ár (unit/passive/base/minimum a pricingType szerint) — DECIMAL-string. */
  primaryPrice: string;
  primaryCurrency: Currency;
  /** Sávok (tiered / base_tiered). */
  tiers: TierFormValues[];
  /** Overflow (sáv-feletti) blokk kapcsoló + mezők (tiered / base_tiered, opcionális). */
  overflowEnabled: boolean;
  overflowBlockSize: string;
  overflowPrice: string;
  overflowCurrency: Currency;
}

/** Üres sáv (tier hozzáadás default): korlátlan felső határ, üres ár, a plan devizája. */
export function emptyTier(currency: Currency = 'HUF'): TierFormValues {
  return { tierMin: '0', tierMax: '', netPrice: '', currency };
}

const asEnum = <T extends string>(
  values: readonly T[],
  v: string | null | undefined,
  fallback: T,
): T => (values.includes(v as T) ? (v as T) : fallback);

const toInt = (s: string): number => Number.parseInt(s, 10);

/**
 * Egy csomag/hozzárendelés ársorai a pricingType→priceType mátrix szerint (flat→unit; commission→minimum;
 * tiered→passive+tier[]+overflow?; base_tiered→base+tier[]+overflow?). A netPrice DECIMAL-string; a tier/overflow
 * egészek. A currency MINDIG kiírva (a legacy klón-bug ellen).
 */
/** JUTALÉKOS árazás ÜRES minimum díjjal — a legacyben megengedett (0-ként tárolva). */
function commissionMinimumOptional(pricing: PricingFormValues): boolean {
  return pricing.pricingType === 'commission' && pricing.primaryPrice.trim() === '';
}

export function buildPriceRows(pricing: PricingFormValues): PriceRow[] {
  const rows: PriceRow[] = [];
  const primaryType = primaryPriceType(pricing.pricingType);
  rows.push({
    type: primaryType,
    // JUTALÉKOS árazás: a minimum díj a legacyben NEM kötelező („a tényleges jutalék külső rendszerből jön") —
    // üresen hagyva a legacy 0-val mentette az ársort. A mátrix ettől is teljesül (pontosan egy `minimum` sor),
    // és a generátor `max(minimum, tényleges)` szabálya 0 minimummal a tényleges jutalékot adja.
    netPrice: commissionMinimumOptional(pricing) ? '0' : pricing.primaryPrice.trim(),
    currency: pricing.primaryCurrency,
  });
  if (hasTiers(pricing.pricingType)) {
    for (const tier of pricing.tiers) {
      rows.push({
        type: 'tier',
        netPrice: tier.netPrice.trim(),
        currency: tier.currency,
        tierMin: toInt(tier.tierMin),
        tierMax: tier.tierMax.trim() === '' ? null : toInt(tier.tierMax),
      });
    }
    if (pricing.overflowEnabled) {
      rows.push({
        type: 'overflow',
        netPrice: pricing.overflowPrice.trim(),
        currency: pricing.overflowCurrency,
        blockSize: toInt(pricing.overflowBlockSize),
      });
    }
  }
  return rows;
}

const DECIMAL_RE = /^\d+(\.\d{1,2})?$/;
const INT_RE = /^\d+$/;

/**
 * A KÖZÖS árazó-blokk kliens-oldali validálása (a katalógus ÉS a hozzárendelés-ár-override közösen használja): az
 * elsődleges ár DECIMAL-formátuma + (sávos típusnál) legalább 1 sáv + soronkénti egész/ár + a sávok monotonitása
 * (növekvő, nem-átfedő; a korlátlan max után nincs több) + az opcionális overflow blokkméret/ár. A `base` a
 * mező-út-prefix (`['plans', i]` a katalógusnál, `['pricing']` a hozzárendelésnél), a `v` a lokalizált üzenet-
 * feloldó (`validation:<modul>.<kulcs>`). A VÉGSŐ fal a szerver (a mátrix/monotonitást a BFF is 422-zi).
 */
export function validatePricingForm(
  pricing: PricingFormValues,
  ctx: zod.RefinementCtx,
  base: (string | number)[],
  v: (k: string) => string,
): void {
  // Elsődleges ár (unit/passive/base/minimum) — kötelező, DECIMAL-formátum (akár 0). KIVÉTEL: a JUTALÉKOS
  // minimum díj üresen hagyható (a legacy 1:1) — a wire-be 0 megy.
  if (!commissionMinimumOptional(pricing) && !DECIMAL_RE.test(pricing.primaryPrice.trim())) {
    ctx.addIssue({
      code: zod.ZodIssueCode.custom,
      path: [...base, 'primaryPrice'],
      message: v('priceFormat'),
    });
  }
  if (!hasTiers(pricing.pricingType)) return;
  // Sávos ár (tiered / base_tiered): legalább 1 sáv + soronkénti + monotonitás.
  if (pricing.tiers.length === 0) {
    ctx.addIssue({
      code: zod.ZodIssueCode.custom,
      path: [...base, 'tiers'],
      message: v('tiersRequired'),
    });
  }
  let prevMax: number | null = null;
  let prevPrice: number | null = null;
  pricing.tiers.forEach((tier, j) => {
    const tb = [...base, 'tiers', j];
    const minOk = INT_RE.test(tier.tierMin.trim());
    const maxSet = tier.tierMax.trim() !== '';
    const priceOk = DECIMAL_RE.test(tier.netPrice.trim());
    if (!minOk) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: [...tb, 'tierMin'],
        message: v('intRequired'),
      });
    }
    if (maxSet && !INT_RE.test(tier.tierMax.trim())) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: [...tb, 'tierMax'],
        message: v('intRequired'),
      });
    }
    if (!priceOk) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: [...tb, 'netPrice'],
        message: v('priceFormat'),
      });
    }
    const min = minOk ? toInt(tier.tierMin) : NaN;
    const max = maxSet && INT_RE.test(tier.tierMax.trim()) ? toInt(tier.tierMax) : null;
    // Soron belül: min < max (ha van felső határ).
    if (max != null && !Number.isNaN(min) && min >= max) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: [...tb, 'tierMax'],
        message: v('tierRange'),
      });
    }
    // Sorok között: a korlátlan (null max) sáv után nem jöhet több; egyébként monoton növekvő, nem átfedő.
    if (!Number.isNaN(min)) {
      if (prevMax === null && j > 0) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: [...tb, 'tierMin'],
          message: v('tierOverlap'),
        });
      } else if (prevMax !== null && min < prevMax) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: [...tb, 'tierMin'],
          message: v('tierOverlap'),
        });
      }
    }
    // Sáv-ár NEM-csökkenés (a BFF `validatePricingRows` monotonitásának tükre — jobb hibajelzés még submit előtt).
    if (priceOk) {
      const price = Number(tier.netPrice.trim());
      if (prevPrice !== null && price < prevPrice) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: [...tb, 'netPrice'],
          message: v('tierPriceDecrease'),
        });
      }
      prevPrice = price;
    }
    prevMax = max;
  });
  if (pricing.overflowEnabled) {
    if (!INT_RE.test(pricing.overflowBlockSize.trim()) || toInt(pricing.overflowBlockSize) < 1) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: [...base, 'overflowBlockSize'],
        message: v('blockSize'),
      });
    }
    if (!DECIMAL_RE.test(pricing.overflowPrice.trim())) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: [...base, 'overflowPrice'],
        message: v('priceFormat'),
      });
    }
  }
}

/**
 * A hozzárendelésnél VÁLASZTHATÓ árazási típusok (down-conversion; a legacy/BFF `allowedPricingTypes` 1:1): a
 * katalógus-plan típusa VAGY `flat` (fix díj). Katalógus `flat`/ismeretlen → CSAK `flat`; egyébként {flat,
 * katalógus-típus}. Felfelé/oldalirányú váltás TILOS (a szerver is 422-zi).
 */
export function allowedPricingTypes(catalogType: string | null | undefined): PricingType[] {
  if (!catalogType || catalogType === 'flat') return ['flat'];
  const catalog = PRICING_TYPES.find((p) => p === catalogType);
  return catalog ? ['flat', catalog] : ['flat'];
}

/** Egy nyers ár-sor (a detail/refs ár-alakja — a mezők a leképezéshez elégségesek). */
export interface RawPriceRow {
  type: string;
  netPrice: string;
  currency: string;
  tierMin?: number | null | undefined;
  tierMax?: number | null | undefined;
  blockSize?: number | null | undefined;
}

/**
 * Ár-sorok (detail/katalógus) → a KÖZÖS árazó-blokk form-mezői (a `pricingType`-ot a hívó adja). A tier-sorokat
 * tierMin szerint rendezi; a korlátlan (null) max → '' a mezőben. A currency ismeretlen értéke HUF-ra esik.
 */
export function pricesToForm(
  prices: RawPriceRow[],
  pricingType: PricingType,
): Omit<PricingFormValues, 'pricingType'> {
  const primaryType = primaryPriceType(pricingType);
  const primary = prices.find((p) => p.type === primaryType);
  const tiers = prices
    .filter((p) => p.type === 'tier')
    .sort((a, b) => (a.tierMin ?? 0) - (b.tierMin ?? 0))
    .map<TierFormValues>((p) => ({
      tierMin: String(p.tierMin ?? 0),
      tierMax: p.tierMax == null ? '' : String(p.tierMax),
      netPrice: p.netPrice,
      currency: asEnum(CURRENCIES, p.currency, 'HUF'),
    }));
  const overflow = prices.find((p) => p.type === 'overflow');
  return {
    primaryPrice: primary?.netPrice ?? '',
    primaryCurrency: asEnum(CURRENCIES, primary?.currency, 'HUF'),
    tiers,
    overflowEnabled: overflow != null,
    overflowBlockSize: overflow?.blockSize != null ? String(overflow.blockSize) : '',
    overflowPrice: overflow?.netPrice ?? '',
    overflowCurrency: asEnum(CURRENCIES, overflow?.currency, 'HUF'),
  };
}
